import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpenCheck, LogOut, Search, Upload, Download, Users, CheckCircle2, AlertCircle, Clock, FileSpreadsheet, ChevronRight, GraduationCap, History, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, clearAuth, getUser, STATUS_META } from "@/lib/api";
import LegerImporterModal from "@/components/LegerImporterModal";
import LegerHistoryModal from "@/components/LegerHistoryModal";
import DriveStatusCard from "@/components/DriveStatusCard";

export default function AdminDashboard() {
  const nav = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState({ total: 0, verified: 0, pending: 0, needs_fix: 0, draft: 0 });
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kelasFilter, setKelasFilter] = useState("all");
  const [kelasList, setKelasList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [legerOpen, setLegerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      // silent
    }
  }, []);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/students", { params: { q: q || undefined, status: statusFilter, kelas: kelasFilter } });
      setStudents(data.items);
    } catch (err) {
      toast.error("Gagal memuat data siswa");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, kelasFilter]);

  const loadKelas = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/kelas");
      setKelasList(data.items);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadKelas();
    loadStudents();
  }, [loadStats, loadKelas, loadStudents]);

  const logout = () => {
    clearAuth();
    nav("/admin/login");
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/leger/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Template_Leger.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal mengunduh template");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/70" data-testid="admin-dashboard-page">
      {/* Sidebar-less top navigation */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2.5">
            <img src="/logo-sekolah.png" alt="Logo" className="w-11 h-11 object-contain" />
            <div>
              <div className="font-display font-bold text-slate-900 leading-none text-sm">SMPN 1 Penengahan</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">Buku Induk Digital • Dashboard TU</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-600" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-1.5" /> Keluar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <DriveStatusCard />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={<Users />} label="Total Siswa" value={stats.total} color="blue" testid="stat-total" />
          <StatCard icon={<CheckCircle2 />} label="Terverifikasi" value={stats.verified} color="emerald" testid="stat-verified" />
          <StatCard icon={<Clock />} label="Menunggu" value={stats.pending} color="amber" testid="stat-pending" />
          <StatCard icon={<AlertCircle />} label="Minta Perbaikan" value={stats.needs_fix} color="rose" testid="stat-needs-fix" />
          <StatCard icon={<GraduationCap />} label="Draft" value={stats.draft} color="slate" testid="stat-draft" />
        </div>

        {/* Kelas overview grid — quick per-class visibility */}
        {kelasList.length > 0 && (
          <Card className="mb-6" data-testid="kelas-overview-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <LayoutGrid className="w-4 h-4 text-blue-700" /> Data per Kelas
                </div>
                <span className="text-xs text-slate-500">Klik salah satu kelas untuk memfilter tabel</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                <KelasChip label="Semua Kelas" total={stats.total} active={kelasFilter === "all"} onClick={() => setKelasFilter("all")} testid="kelas-chip-all" />
                {kelasList.map((k) => (
                  <KelasChip
                    key={k.kelas}
                    label={k.kelas}
                    total={k.total}
                    verified={k.verified}
                    needsFix={k.needs_fix}
                    pending={k.pending}
                    active={kelasFilter === k.kelas}
                    onClick={() => setKelasFilter(k.kelas)}
                    testid={`kelas-chip-${k.kelas}`}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Toolbar */}
        <Card className="mb-6">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Cari nama, NISN, atau NIS..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadStudents()}
                className="pl-9 h-10"
                data-testid="search-input"
              />
            </div>
            <Select value={kelasFilter} onValueChange={(v) => setKelasFilter(v)}>
              <SelectTrigger className="w-full md:w-44 h-10" data-testid="kelas-filter-select"><SelectValue placeholder="Semua Kelas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kelas</SelectItem>
                {kelasList.map((k) => (
                  <SelectItem key={k.kelas} value={k.kelas}>{k.kelas} ({k.total})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
              <SelectTrigger className="w-full md:w-48 h-10" data-testid="status-filter-select"><SelectValue placeholder="Semua Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="verified">Terverifikasi</SelectItem>
                <SelectItem value="pending">Menunggu Verifikasi</SelectItem>
                <SelectItem value="needs_fix">Minta Perbaikan</SelectItem>
                <SelectItem value="draft">Belum Lengkap</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={loadStudents} variant="outline" className="h-10" data-testid="apply-filter-button">Terapkan</Button>
            <div className="md:ml-auto flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setHistoryOpen(true)} className="h-10" data-testid="open-history-button">
                <History className="w-4 h-4 mr-1.5" /> Riwayat
              </Button>
              <Button variant="outline" onClick={downloadTemplate} className="h-10" data-testid="download-template-button">
                <Download className="w-4 h-4 mr-1.5" /> Template
              </Button>
              <Button onClick={() => setLegerOpen(true)} className="bg-blue-700 hover:bg-blue-800 h-10" data-testid="open-leger-import-button">
                <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Impor Leger
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Students Table */}
        <Card>
          <CardContent className="p-0">
            <Table data-testid="students-table">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Nama Lengkap</TableHead>
                  <TableHead>NISN</TableHead>
                  <TableHead>Kelas</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Diperbarui</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">Memuat data...</TableCell></TableRow>
                ) : students.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500" data-testid="empty-state">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="w-8 h-8 text-slate-300" />
                      <div className="text-sm">Belum ada data siswa</div>
                      <div className="text-xs">Bagikan link <Link to="/siswa" className="text-blue-700 underline">Portal Siswa</Link> atau impor Leger untuk memulai</div>
                    </div>
                  </TableCell></TableRow>
                ) : (
                  students.map((s, i) => {
                    const meta = STATUS_META[s.status] || STATUS_META.draft;
                    return (
                      <TableRow key={s.id} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => nav(`/admin/siswa/${s.id}`)} data-testid={`student-row-${s.nisn}`}>
                        <TableCell className="text-slate-500 font-mono text-xs">{i + 1}</TableCell>
                        <TableCell className="font-medium text-slate-900">{s.nama_lengkap || <span className="text-slate-400 italic">Belum diisi</span>}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-600">{s.nisn}</TableCell>
                        <TableCell className="text-slate-600">{s.kelas || "-"}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.cls}`}>
                            <span className={`w-1 h-1 rounded-full ${meta.dot}`} />
                            {meta.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">{s.updated_at ? new Date(s.updated_at).toLocaleDateString("id-ID", { year: "numeric", month: "short", day: "numeric" }) : "-"}</TableCell>
                        <TableCell className="text-right">
                          <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      <LegerImporterModal open={legerOpen} onOpenChange={setLegerOpen} onSuccess={() => { loadStats(); loadKelas(); loadStudents(); }} />
      <LegerHistoryModal open={historyOpen} onOpenChange={setHistoryOpen} />
    </div>
  );
}

const KelasChip = ({ label, total, verified, needsFix, pending, active, onClick, testid }) => {
  const hasIssues = (needsFix || 0) > 0 || (pending || 0) > 0;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={
        "flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-all " +
        (active
          ? "border-blue-700 bg-blue-700 text-white shadow-md"
          : hasIssues
          ? "border-amber-200 bg-amber-50 text-slate-800 hover:border-amber-400"
          : "border-slate-200 bg-white text-slate-800 hover:border-blue-400 hover:bg-slate-50")
      }
    >
      <div className={"text-xs font-semibold uppercase tracking-wider " + (active ? "text-blue-100" : "text-slate-500")}>
        {label}
      </div>
      <div className="text-lg font-bold font-display">{total || 0}</div>
      {(verified !== undefined || needsFix !== undefined) && (
        <div className={"flex items-center gap-2 text-[10px] font-medium " + (active ? "text-blue-100" : "")}>
          {verified > 0 && <span className={active ? "" : "text-emerald-600"}>✓ {verified}</span>}
          {pending > 0 && <span className={active ? "" : "text-amber-600"}>◷ {pending}</span>}
          {needsFix > 0 && <span className={active ? "" : "text-rose-600"}>⚠ {needsFix}</span>}
        </div>
      )}
    </button>
  );
};

const StatCard = ({ icon, label, value, color, testid }) => {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className={`w-10 h-10 rounded-lg grid place-items-center mb-3 border ${colorMap[color]}`}>
          {icon}
        </div>
        <div className="text-2xl font-bold text-slate-900 font-display leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1.5 font-medium">{label}</div>
      </CardContent>
    </Card>
  );
};
